import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { EditMonitorFooter } from '@security/monitors/monitor-form/EditMonitorFooter';
import { EditOutlined } from '@ant-design/icons';
import { MonitorFormTypes } from '@sonar-software/sonar-core';
import { monitorMenuConfigs } from './monitor-form/MonitorMenu';
import { SonarText } from '@ui/text';
import { When } from 'react-if';

import { Card, Space } from 'antd';
import { MonitorForm, MonitorFormRef } from './monitor-form/MonitorForm';
import React, { useCallback, useEffect, useRef } from 'react';
import { useBoolean, useForm, usePrevious } from '@hooks';

export type MonitorCardProps = {
    monitor: MonitorFormTypes
	onCancel?: () => void
	onSave?: () => void
};

export const MonitorCard: React.FC<MonitorCardProps> = ({
	monitor,
	onCancel,
	onSave
}) => {
	const { form } = useForm<MonitorFormTypes>({
		required: [ 'applicationId', 'monitorConfig.monitorTypeId', 'name' ]
	});

	const ref = useRef<MonitorFormRef>(null);
	const previous = usePrevious(monitor);

	const [ editing, setEditing ] = useBoolean(Boolean(!monitor.id));
	const monitorConfig = monitorMenuConfigs.find((config) => config.type === monitor.monitorConfig?.monitorTypeId);

	useEffect(() => {
		if (monitor !== previous) {
			form.setFieldsValue(monitor);
		}
	}, [ monitor, previous ]);

	const getName = useCallback(() => {
		if (!monitor.applicationId || !monitor.name) {
			return COPY_SPECIFIC.CREATE_NEW_MONITOR;
		}

		return editing ? COPY_SPECIFIC.EDITING_LABEL(monitor.name) : monitor.name;
	}, [ monitor, editing ]);

	const handleCancel = useCallback(() => {
		setEditing.off();

		if (onCancel) {
			onCancel();
		}

		form.reset();

	}, [ form, onCancel, setEditing ]);

	const handleSave = useCallback(() => {
		ref?.current?.save();
		setEditing.off();

		if (onSave) {
			onSave();
		}

	}, [ ref, onSave, setEditing ]);

	const handleDelete = useCallback(() => {
		setEditing.off();
		ref?.current?.delete();
	}, [ ref, setEditing ]);

	return (
		<Card
			className={ classNames('monitor-card', { 'can-edit': !editing }) }
			extra={
				<When condition={ !editing }>
					<EditOutlined className='edit-icon' />
				</When>
			}
			onClick={ editing ? undefined : () => setEditing.on() }
			title={
				<SonarText className='h3'>
					{ getName() }
				</SonarText>
			}
			variant='borderless'>
			<Space
				className='monitor-card-body'
				direction='vertical'>
				<MonitorForm
					editing={ editing }
					form={ form }
					hideApplications
					menuOptions='applicationRequired'
					monitor={ monitor }
					ref={ ref } />
				<When condition={ editing }>
					<EditMonitorFooter
						disableSave={ !form.isValid }
						monitor={ monitor }
						onCancel={ handleCancel }
						onDelete={
							monitorConfig?.intrinsic ?
								undefined :
								() => handleDelete()
						}
						onSave={ handleSave } />
				</When>
			</Space>
		</Card>
	);
};


